import type { FetchError } from '@onyx/utils/fetch';

/**
 * Classifies a Helix submission failure so we can show the right guidance:
 * - `transitory` failures are safe to retry and are likely to succeed later.
 * - `permanent` failures will not succeed on retry and need investigation.
 */
export type HelpHubErrorClassification = 'transitory' | 'permanent';

/**
 * Which team owns resolving a submission failure, so the Help Desk knows where
 * to route the ticket:
 * - `help-hub` failures are owned by the Help Hub team (e.g. validation, form,
 *   catalogue, auth or connectivity issues — either our issue or self-resolving
 *   on retry).
 * - `ktsl` failures are genuine Helix data rejections owned by KTSL/Helix
 *   (invalid contact or incident location). The colleague quotes the error
 *   code to the Help Desk, who route these to KTSL/Helix.
 */
export type HelpHubErrorOwner = 'help-hub' | 'ktsl';

type ResponseError = Extract<FetchError, { kind: 'response-error' }>;

/** Help Desk phone number colleagues can call to troubleshoot submissions. */
export const HELP_DESK_PHONE_NUMBER = '020 8718 5999';

const HH_API_ERROR_CODE_HEADER = 'HH-Api-Error-Code';

/**
 * Machine-readable 422 reasons returned by the Help Hub API through the
 * `HH-Api-Error-Code` response header.
 */
export type HelpHubApiErrorReason =
  | 'catalog-mismatch'
  | 'contact-invalid'
  | 'incident-location-invalid'
  | 'unsupported-custom-fields';

/**
 * Every submission failure we can trap and map to a stable error code.
 */
export type SubmissionErrorReason =
  | HelpHubApiErrorReason
  | 'unknown-validation'
  | 'unauthorized'
  | 'timeout'
  | 'server-error'
  | 'bad-request'
  | 'connection'
  | 'unknown';

type SubmissionErrorDefinition = {
  /**
   * Stable, fixed-length 4-digit numeric code a colleague can read out to the
   * Help Desk. Numeric codes are easier to capture and recognise over the
   * phone than words.
   */
  code: string;
  classification: HelpHubErrorClassification;
  /**
   * Team that owns resolving this failure, so the Help Desk knows where to
   * route the ticket. Only genuine Helix data rejections (`2002` contact
   * invalid and `2003` incident location invalid) are owned by KTSL/Helix;
   * every other failure is owned by Help Hub. The Help Desk rule is simply:
   * codes `2002` and `2003` go to KTSL, everything else comes to us.
   */
  owner: HelpHubErrorOwner;
};

/**
 * Lookup table mapping each trappable submission failure to a stable error
 * code and its classification. The code lets Help Desk agents troubleshoot a
 * specific failure without needing to reproduce it.
 */
export const submissionErrorLookup: Record<
  SubmissionErrorReason,
  SubmissionErrorDefinition
> = {
  'catalog-mismatch': {
    code: '2001',
    classification: 'transitory',
    owner: 'help-hub',
  },
  'contact-invalid': {
    code: '2002',
    classification: 'permanent',
    owner: 'ktsl',
  },
  'incident-location-invalid': {
    code: '2003',
    classification: 'permanent',
    owner: 'ktsl',
  },
  'unsupported-custom-fields': {
    code: '2004',
    classification: 'permanent',
    owner: 'help-hub',
  },
  'unknown-validation': {
    code: '2005',
    classification: 'permanent',
    owner: 'help-hub',
  },
  unauthorized: {
    code: '2006',
    classification: 'transitory',
    owner: 'help-hub',
  },
  'bad-request': {
    code: '2007',
    classification: 'permanent',
    owner: 'help-hub',
  },
  connection: {
    code: '2008',
    classification: 'transitory',
    owner: 'help-hub',
  },
  unknown: {
    code: '2099',
    classification: 'permanent',
    owner: 'help-hub',
  },
  timeout: {
    code: '3001',
    classification: 'transitory',
    owner: 'help-hub',
  },
  'server-error': {
    code: '3002',
    classification: 'transitory',
    owner: 'help-hub',
  },
};

/**
 * Builds the user-facing message for a submission failure. The colleague is
 * asked to retry and, if the problem persists, to call the Help Desk and quote
 * the stable error code so the desk can trap and route the specific failure.
 *
 * The `classification` is retained for telemetry and Help Desk routing but does
 * not change the copy: colleagues see a single, consistent message.
 */
export const buildHelpHubErrorMessage = (code: string): string =>
  `We're having trouble completing your request. Please try again. If the problem persists, call the Help Desk on ${HELP_DESK_PHONE_NUMBER} and quote error code ${code}.`;

export const buildSubmissionErrorMessage = (
  _classification: HelpHubErrorClassification,
  code: string
): string => buildHelpHubErrorMessage(code);

const isKnownApiReason = (
  value: string | null
): value is HelpHubApiErrorReason =>
  value === 'catalog-mismatch' ||
  value === 'contact-invalid' ||
  value === 'incident-location-invalid' ||
  value === 'unsupported-custom-fields';

const resolveSubmissionErrorReason = (
  status: number,
  error?: ResponseError
): SubmissionErrorReason => {
  if (status === 422) {
    const reason = error?.headers.get(HH_API_ERROR_CODE_HEADER) ?? null;

    return isKnownApiReason(reason) ? reason : 'unknown-validation';
  }

  if (status === 504) {
    return 'timeout';
  }

  if (status >= 500) {
    return 'server-error';
  }

  if (status === 401 || status === 403) {
    return 'unauthorized';
  }

  if (status >= 400) {
    return 'bad-request';
  }

  return 'unknown';
};

export type ResolvedSubmissionError = {
  code: string;
  classification: HelpHubErrorClassification;
  owner: HelpHubErrorOwner;
  message: string;
  reason: SubmissionErrorReason;
};

/**
 * Maps a Helix submission response error to its stable code, classification and
 * user-facing message.
 */
export const resolveSubmissionResponseError = (
  status: number,
  error?: ResponseError
): ResolvedSubmissionError => {
  const reason = resolveSubmissionErrorReason(status, error);
  const { code, classification, owner } = submissionErrorLookup[reason];

  return {
    code,
    classification,
    owner,
    reason,
    message: buildSubmissionErrorMessage(classification, code),
  };
};

/**
 * Maps a network/connection failure (no HTTP response) to its stable code,
 * classification and user-facing message.
 */
export const resolveSubmissionConnectionError = (): ResolvedSubmissionError => {
  const reason: SubmissionErrorReason = 'connection';
  const { code, classification, owner } = submissionErrorLookup[reason];

  return {
    code,
    classification,
    owner,
    reason,
    message: buildSubmissionErrorMessage(classification, code),
  };
};

/**
 * Every chatbot (agent) failure we can trap and map to a stable error code.
 * Chatbot codes live in the `4xxx` range so the Help Desk can tell them apart
 * from issue submission failures (`2xxx` / `3xxx`).
 */
export type ChatbotErrorReason =
  | 'unauthorized'
  | 'timeout'
  | 'server-error'
  | 'bad-request'
  | 'connection'
  | 'empty-response'
  | 'unknown';

type ChatbotErrorDefinition = {
  code: string;
  classification: HelpHubErrorClassification;
};

export const chatbotErrorLookup: Record<
  ChatbotErrorReason,
  ChatbotErrorDefinition
> = {
  unauthorized: { code: '4001', classification: 'transitory' },
  'bad-request': { code: '4002', classification: 'permanent' },
  connection: { code: '4003', classification: 'transitory' },
  'empty-response': { code: '4004', classification: 'transitory' },
  timeout: { code: '4005', classification: 'transitory' },
  'server-error': { code: '4006', classification: 'transitory' },
  unknown: { code: '4099', classification: 'permanent' },
};

export type ResolvedChatbotError = {
  code: string;
  classification: HelpHubErrorClassification;
  reason: ChatbotErrorReason;
  message: string;
};

const resolveChatbotErrorReason = (status: number): ChatbotErrorReason => {
  if (status === 504) {
    return 'timeout';
  }

  if (status >= 500) {
    return 'server-error';
  }

  if (status === 401 || status === 403) {
    return 'unauthorized';
  }

  if (status >= 400) {
    return 'bad-request';
  }

  return 'unknown';
};

/**
 * Maps a chatbot failure to its stable code, classification and user-facing
 * message. Pass the HTTP `status` when the agent responded, otherwise pass a
 * `reason` for non-HTTP failures (connection, empty response, unknown).
 */
export const resolveChatbotError = (
  input: { status: number } | { reason: ChatbotErrorReason }
): ResolvedChatbotError => {
  const reason =
    'status' in input ? resolveChatbotErrorReason(input.status) : input.reason;
  const { code, classification } = chatbotErrorLookup[reason];

  return {
    code,
    classification,
    reason,
    message: buildHelpHubErrorMessage(code),
  };
};
